import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import { AxiosError } from 'axios';
import { getToken } from '../tokenprovider';

// TypeScript interfaces for 2FA operations
export interface SendEmail2FARequest {
  email: string;
}

export interface SendEmail2FAResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    codeExpiry?: string;
    requestId?: string;
  };
}

export interface VerifyEmail2FARequest {
  email: string;
  code: string;
}

export interface VerifyEmail2FAResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    twoFactorEnabled: boolean;
    backupCodes?: string[];
  };
}

export interface TwoFactorError {
  message: string;
  code?: string;
  details?: any;
}

/**
 * Send verification code to user's email for 2FA setup
 * @param email - User's email address
 * @returns Promise with API response
 */
export const sendEmail2FA = async (
  email: string
): Promise<SendEmail2FAResponse> => {
  try {
    console.log(`Sending 2FA verification code to email: ${email}`);

    // Check authentication
    const token = getToken();
    console.log('Authentication token available:', !!token);
    if (!token) {
      console.warn('No authentication token found - user may not be logged in');
    }

    const requestData: SendEmail2FARequest = {
      email: email.trim().toLowerCase(),
    };

    console.log('Request data:', requestData);
    console.log('API endpoint:', Path.sendEmail2FA);
    console.log(
      'Full URL:',
      `${import.meta.env.VITE_APP_BACKEND_PORT}${Path.sendEmail2FA}`
    );

    // TEMPORARY: Uncomment the lines below to use mock response while debugging backend
    // console.log('🚧 Using mock response for testing');
    // return {
    //   success: true,
    //   message: 'Mock: Verification code sent to your email',
    //   data: { email }
    // };

    const response = await apiClient.post(
      import.meta.env.VITE_APP_BACKEND_USER + Path.sendEmail2FA,
      requestData
    );

    if (response.status === 200 || response.status === 201) {
      console.log('2FA email sent successfully:', response.data);
      return {
        success: true,
        message:
          response.data.message || 'Verification code sent to your email',
        data: response.data.data || { email },
      };
    }

    // Handle unexpected success status codes
    console.warn('Unexpected response status:', response.status);
    return {
      success: false,
      message: 'Unexpected response from server',
    };
  } catch (error) {
    console.error('Error sending 2FA email:', error);

    if (error instanceof AxiosError) {
      // Log detailed error information for debugging
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Response headers:', error.response?.headers);
      console.error('Request config:', error.config);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.details ||
        `Server error (${error.response?.status}): ${error.message}`;

      // Handle specific status codes
      if (error.response?.status === 500) {
        console.error('Internal Server Error - Check backend logs');
        return {
          success: false,
          message: `Server error: ${errorMessage}. Please check backend logs for details.`,
          data: { email },
        };
      }

      return {
        success: false,
        message: errorMessage,
        data: { email },
      };
    }

    return {
      success: false,
      message: 'Network error occurred while sending verification code',
    };
  }
};

/**
 * Verify email 2FA code and enable two-factor authentication
 * @param email - User's email address
 * @param code - Verification code from email
 * @returns Promise with API response
 */
export const verifyEmail2FA = async (
  email: string,
  code: string
): Promise<VerifyEmail2FAResponse> => {
  try {
    console.log(`Verifying 2FA code for email: ${email}`);

    const requestData: VerifyEmail2FARequest = {
      email: email.trim().toLowerCase(),
      code: code.trim(),
    };

    const response = await apiClient.post(
      import.meta.env.VITE_APP_BACKEND_USER + Path.verifyEmail2FA,
      requestData
    );

    if (response.status === 200 || response.status === 201) {
      console.log('2FA verification successful:', response.data);
      return {
        success: true,
        message:
          response.data.message ||
          'Two-factor authentication enabled successfully',
        data: response.data.data || {
          email,
          twoFactorEnabled: true,
        },
      };
    }

    // Handle unexpected success status codes
    console.warn('Unexpected response status:', response.status);
    return {
      success: false,
      message: 'Unexpected response from server',
    };
  } catch (error) {
    console.error('Error verifying 2FA code:', error);

    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to verify code';

      // Handle specific error cases
      if (error.response?.status === 400) {
        return {
          success: false,
          message: errorMessage || 'Invalid verification code',
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Verification code not found or expired',
        };
      }

      if (error.response?.status === 429) {
        return {
          success: false,
          message: 'Too many attempts. Please try again later',
        };
      }

      return {
        success: false,
        message: errorMessage,
      };
    }

    return {
      success: false,
      message: 'Network error occurred while verifying code',
    };
  }
};

/**
 * Check if 2FA is enabled for the current user
 * @returns Promise with 2FA status
 */
export const check2FAStatus = async (): Promise<{
  enabled: boolean;
  email?: string;
}> => {
  try {
    const response = await apiClient.get(
      import.meta.env.VITE_APP_BACKEND_USER + Path.check2FAStatus
    );

    if (response.status === 200) {
      return {
        enabled: response.data.enabled || false,
        email: response.data.email,
      };
    }

    return { enabled: false };
  } catch (error) {
    console.error('Error checking 2FA status:', error);
    return { enabled: false };
  }
};

/**
 * Disable 2FA for the current user
 * @returns Promise with disable operation result
 */
export const disable2FA = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await apiClient.post(
      import.meta.env.VITE_APP_BACKEND_USER + Path.disable2FA
    );

    if (response.status === 200) {
      return {
        success: true,
        message:
          response.data.message ||
          'Two-factor authentication disabled successfully',
      };
    }

    return {
      success: false,
      message: 'Failed to disable two-factor authentication',
    };
  } catch (error) {
    console.error('Error disabling 2FA:', error);

    if (error instanceof AxiosError) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to disable two-factor authentication';
      return {
        success: false,
        message: errorMessage,
      };
    }

    return {
      success: false,
      message: 'Network error occurred while disabling 2FA',
    };
  }
};

/**
 * Validate email format
 * @param email - Email to validate
 * @returns boolean indicating if email is valid
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate verification code format
 * @param code - Code to validate
 * @returns boolean indicating if code format is valid
 */
export const validateVerificationCode = (code: string): boolean => {
  // Assuming 6-digit numeric code
  const codeRegex = /^\d{6}$/;
  return codeRegex.test(code.trim());
};

// ==========================================
// MFA TOTP Implementation
// ==========================================

export interface MfaStatusResponse {
  success: boolean;
  data: {
    preferred: string | null;
    enabled: {
      software: boolean;
      sms: boolean;
    };
    mfaEnabled: boolean;
  };
}

export interface MfaAssociateResponse {
  success: boolean;
  message: string;
  data: {
    secretCode: string;
    otpAuthUri: string;
    session?: string;
    // Backward-compatible aliases for existing UI usage.
    secret: string;
    qrCodeUrl: string;
  };
}

export interface MfaVerifyResponse {
  success: boolean;
  message: string;
}

export interface MfaDisableResponse {
  success: boolean;
  message: string;
}

export interface MfaRecoveryCodesResponse {
  success: boolean;
  message: string;
  data?: {
    codes: string[];
  };
}

export interface RecoverMfaLoginResponse {
  success: boolean;
  sessionId: string;
  statusId: string;
  message?: string;
}

/**
 * Get current MFA status (TOTP)
 */
export const getMfaStatus = async (): Promise<MfaStatusResponse> => {
  try {
    const response = await apiClient.get(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaStatus}`
    );
    const payload = response.data || {};
    const enabledSoftware = !!payload?.data?.enabled?.software;

    return {
      success: !!payload?.success,
      data: {
        preferred: payload?.data?.preferred ?? null,
        enabled: {
          software: enabledSoftware,
          sms: !!payload?.data?.enabled?.sms,
        },
        mfaEnabled: enabledSoftware,
      },
    };
  } catch (error) {
    console.error('Error fetching MFA status:', error);
    throw error;
  }
};

/**
 * Associate a new TOTP device (get secret/QR code)
 */
export const associateMfa = async (): Promise<MfaAssociateResponse> => {
  try {
    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaAssociate}`
    );
    const payload = response.data || {};
    const rawData = payload?.data || {};

    return {
      success: !!payload?.success,
      message: payload?.message || 'TOTP secret generated',
      data: {
        secretCode: rawData?.secretCode || '',
        otpAuthUri: rawData?.otpAuthUri || '',
        session: rawData?.session,
        // Backward-compatible aliases
        secret: rawData?.secretCode || '',
        qrCodeUrl: rawData?.otpAuthUri || '',
      },
    };
  } catch (error) {
    console.error('Error associating MFA:', error);
    throw error;
  }
};

/**
 * Verify TOTP code to enable MFA
 * @param code - 6-digit TOTP code
 */
export const verifyMfa = async (
  code: string,
  session?: string
): Promise<MfaVerifyResponse> => {
  try {
    const payload: { code: string; session?: string } = { code };
    if (session) {
      payload.session = session;
    }

    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaVerify}`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Error verifying MFA code:', error);
    throw error;
  }
};

/**
 * Disable MFA (TOTP)
 */
export const disableMfa = async (): Promise<MfaDisableResponse> => {
  try {
    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaDisable}`,
      { mode: 'software' }
    );
    return response.data;
  } catch (error) {
    console.error('Error disabling MFA:', error);
    throw error;
  }
};

export const generateMfaRecoveryCodes =
  async (): Promise<MfaRecoveryCodesResponse> => {
    try {
      const response = await apiClient.post(
        `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaRecoveryCodes}`
      );
      return response.data;
    } catch (error) {
      console.error('Error generating MFA recovery codes:', error);
      throw error;
    }
  };

export const recoverLoginWithMfaCode = async (
  email: string,
  password: string,
  recoveryCode: string,
  client: 'web' | 'mobile' = 'web'
): Promise<RecoverMfaLoginResponse> => {
  try {
    const response = await apiClient.post(
      `${import.meta.env.VITE_APP_BACKEND_USER}/${Path.mfaRecoverLogin}?client=${client}`,
      {
        email: email.trim().toLowerCase(),
        password,
        recoveryCode: recoveryCode.trim().toUpperCase(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error recovering login with MFA code:', error);
    throw error;
  }
};
